/**
 * The second dropdown on an app page — jump between the apps in the same category without
 * going back to the hub. A Maritime Dropdown (a menu of navigations, not a form select).
 *
 * Scoped to the current app's category on purpose: the breadcrumb above it says
 * "Compliance: Survey Monitoring", so the switcher offers the rest of Compliance. The full
 * list is the sidebar's job and is one click away.
 *
 * Renders nothing when the category has no other app in it — a menu with one entry is a
 * control that lies about being useful.
 */

import { useNavigate } from 'react-router-dom';
import { Check, ChevronDown } from 'lucide-react';
import { Dropdown, VisuallyHidden } from '@syia-ai/maritimeai-ui';
import type { HubApp } from '@/hooks/useAgentHubApps';

interface AppSwitcherProps {
  current: HubApp;
  siblings: HubApp[];
}

export default function AppSwitcher({ current, siblings }: AppSwitcherProps) {
  const navigate = useNavigate();

  if (siblings.length < 2) return null;

  return (
    <Dropdown className="shell-app-switcher">
      <Dropdown.Trigger
        variant="secondary"
        className="shell-app-switcher__trigger"
        endIcon={<ChevronDown />}
        aria-label={`Switch app — current: ${current.label}`}
      >
        <span className="shell-app-switcher__label">{current.label}</span>
      </Dropdown.Trigger>

      <Dropdown.Menu align="end" className="shell-app-switcher__menu">
        {siblings.map((app) => {
          const isCurrent = app.href === current.href;
          return (
            <Dropdown.Item
              key={app.id}
              className="shell-app-switcher__item"
              onSelect={() => {
                if (!isCurrent) navigate(app.href);
              }}
            >
              <span>{app.label}</span>
              {isCurrent && (
                <>
                  <Check className="shell-app-switcher__check" aria-hidden />
                  <VisuallyHidden>(current)</VisuallyHidden>
                </>
              )}
            </Dropdown.Item>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
}
